import { GuideButton } from "@/components/GuideButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, RefreshCw, Store, TrendingUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useSort } from "@/lib/sort";

const FIRST_DESC = { sale: "desc", cost: "desc", margin: "desc", margin_pct: "desc", orders: "desc", pieces: "desc", last_order: "desc", created_at: "desc", total: "desc", total_spent: "desc", orders_count: "desc" };
const SHOP_COLS = { name: (s) => s.name, orders: (s) => s.orders, pieces: (s) => s.pieces, sale: (s) => s.sale, cost: (s) => s.cost, margin: (s) => s.margin, margin_pct: (s) => s.margin_pct, last_order: (s) => s.last_order };
const STATUS_ORDER = ["in_attesa_pagamento", "ricevuto", "pagato", "in_produzione", "spedito", "completato", "annullato"];
const stIdx = (s) => { const i = STATUS_ORDER.indexOf(s); return i < 0 ? 99 : i; };
const pieces = (items) => items.reduce((n, i) => n + (+i.quantity || 0), 0);
const ORDER_COLS = { number: (o) => o.number, created_at: (o) => o.created_at, items: (o) => pieces(o.lines), sale: (o) => o.sale, cost: (o) => o.cost, margin: (o) => o.margin, status: (o) => stIdx(o.status) };
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, eur, fmtDate, downloadFile, errMsg } from "@/lib/api";
import { StatusBadge } from "@/pages/Account";

export function ManagerLayout() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user || user.role !== "manager") return <Navigate to="/accedi?next=/manager" replace />;
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="manager-panel"><Outlet /></div>;
}

const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const mLabel = (k) => `${MONTHS[+k.slice(5) - 1]} ${k.slice(2, 4)}`;

function Kpis({ s, prefix }) {
  const cards = [["Ordini", s.orders, "orders"], ["Pezzi", s.pieces, "pieces"], ["Venduto a listino sito", eur(s.sale), "sale"],
    ["Costo d'acquisto riservato", eur(s.cost), "cost"], ["Margine", eur(s.margin), "margin"], ["Margine %", `${s.margin_pct}%`, "margin-pct"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
      {cards.map(([l, v, k]) => (
        <div key={k} className={`rounded-lg border p-4 ${k.startsWith("margin") ? "bg-brand-navy text-white border-brand-navy" : "bg-white"}`}>
          <div className={`text-[11px] font-mono uppercase ${k.startsWith("margin") ? "text-brand-yellow" : "text-slate-500"}`}>{l}</div>
          <div className="text-xl font-bold" data-testid={`${prefix}-kpi-${k}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function Charts({ s }) {
  return (
    <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
      <div className="rounded-xl border bg-white p-4" data-testid="manager-monthly-chart">
        <div className="font-mono text-[11px] uppercase text-slate-500 mb-2">Andamento mensile (ultimi 12 mesi)</div>
        {s.monthly.length ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.monthly.map((m) => ({ ...m, label: mLabel(m.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={50} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="cost" name="Acquisto" fill="#0A2E5C" radius={[3, 3, 0, 0]} />
              <Bar dataKey="margin" name="Margine" fill="#EDD93F" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-sm text-slate-500 py-10 text-center">Ancora nessun ordine.</p>}
      </div>
      <div className="rounded-xl border bg-white p-4" data-testid="manager-top-products">
        <div className="font-mono text-[11px] uppercase text-slate-500 mb-2">Timbri più ordinati</div>
        {s.top_products.map((p, i) => (
          <div key={p.name} className="flex justify-between gap-2 text-sm py-1.5 border-b last:border-0">
            <span className="truncate"><b className="text-brand-navy">{i + 1}.</b> {p.name}</span>
            <span className="whitespace-nowrap text-slate-600">{p.quantity} pz · <b>{eur(p.margin)}</b></span>
          </div>
        ))}
        {!s.top_products.length && <p className="text-sm text-slate-500">—</p>}
      </div>
    </div>
  );
}

function SyncBox({ onDone }) {
  const [s, setS] = useState(null);
  const done = useRef(null);
  const running = s?.last?.status === "running";
  const load = () => api.get("/manager/sync").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => api.get("/manager/sync").then((r) => {
      setS(r.data);
      if (r.data.last?.status !== "running") {
        const id = r.data.last?.id;
        if (done.current === id) return;
        done.current = id;
        if (r.data.last?.status === "done") { toast.success("Sincronizzazione completata", { id }); onDone(); } else toast.error(r.data.last?.error || "Sincronizzazione non riuscita", { id });
      }
    }), 2500);
    return () => clearInterval(t);
  }, [running, onDone]);
  const start = () => api.post("/manager/sync").then(load).catch((e) => toast.error(errMsg(e)));
  const toggle = (v) => api.put("/manager/sync", { auto_daily: v }).then((r) => { setS(r.data); toast.success(v ? "Sincronizzazione automatica attivata" : "Sincronizzazione automatica disattivata"); }).catch((e) => toast.error(errMsg(e)));
  if (!s) return null;
  const st = s.last?.stats || {};
  return (
    <div className="rounded-xl border bg-white p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="manager-sync-box">
      <button data-testid="manager-sync-btn" disabled={running} onClick={start} className="press h-11 px-5 rounded-full bg-brand-yellow text-brand-navy font-bold flex items-center gap-2 disabled:opacity-60 shrink-0">
        <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} /> {running ? "Sincronizzazione…" : "Sincronizza"}
      </button>
      <div className="text-sm flex-1 min-w-0" data-testid="manager-sync-last">
        {running ? <span>Scarico gli ordini da halfatimbri.it… {st.processed || 0}{s.last.total ? ` / ${s.last.total}` : ""}</span>
          : s.last ? <span>Ultima sincronizzazione: <b>{fmtDate(s.last.finished_at || s.last.created_at)}</b>{s.last.status === "done" ? ` · ${st.created || 0} nuovi, ${st.updated || 0} aggiornati` : " · non riuscita"}</span>
            : <span className="text-slate-500">Mai sincronizzato. Premi Sincronizza per scaricare gli ordini.</span>}
      </div>
      <label className="flex items-center gap-2 text-sm shrink-0">
        <Switch data-testid="manager-auto-sync-switch" checked={s.auto_daily} onCheckedChange={toggle} /> Automatica {s.schedule}
      </label>
    </div>
  );
}

const dlList = (l, fmt) => downloadFile({ url: `/manager/price-lists/${l.id}/file`, params: { fmt } }, `listino-${l.name}.${fmt}`).catch((e) => toast.error(errMsg(e)));

function PriceListHeaderBtn({ lists }) {
  if (!lists?.length) return null;
  const one = lists.length === 1;
  const go = () => (one ? dlList(lists[0], "pdf") : document.getElementById("manager-price-lists")?.scrollIntoView({ behavior: "smooth", block: "center" }));
  return (
    <button data-testid="manager-download-price-list-btn" onClick={go}
      className="press h-12 px-6 rounded-full bg-brand-yellow text-brand-navy font-bold flex items-center gap-2 shadow-md hover:bg-brand-yellowdark shrink-0">
      <FileDown className="w-5 h-5" /> {one ? "Scarica il mio listino" : `I miei listini (${lists.length})`}
    </button>
  );
}

function ManagerPriceLists({ lists }) {
  if (!lists) return null;
  return (
    <div id="manager-price-lists" className="rounded-xl border-2 border-brand-navy bg-brand-navy text-white p-5 space-y-3" data-testid="manager-price-lists">
      <div className="flex items-center gap-2 text-lg font-bold uppercase"><FileDown className="w-5 h-5 text-brand-yellow" /> Il tuo listino riservato</div>
      {!lists.length && <p className="text-sm text-white/80" data-testid="manager-price-lists-empty">Nessun listino ti è stato ancora assegnato. Contatta l'amministratore.</p>}
      {lists.map((l, i) => (
        <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white/10 px-4 py-3" data-testid={`manager-price-list-${i}`}>
          <div><b className="text-base">{l.name}</b><div className="text-xs text-white/70">{l.products} prodotti{l.valid_until && ` · valido fino al ${l.valid_until.split("-").reverse().join("/")}`}</div></div>
          <div className="flex gap-2">
            <button data-testid={`manager-price-list-pdf-${i}`} onClick={() => dlList(l, "pdf")} className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-bold flex items-center gap-1.5"><FileDown className="w-4 h-4" /> Scarica PDF</button>
            <button data-testid={`manager-price-list-csv-${i}`} onClick={() => dlList(l, "csv")} className="press h-10 px-4 rounded-md border border-white/60 text-white text-sm font-semibold flex items-center gap-1.5"><FileDown className="w-4 h-4" /> CSV</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ManagerOverview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [lists, setLists] = useState(null);
  useEffect(() => { api.get("/manager/price-lists").then((r) => setLists(r.data)).catch(() => setLists([])); }, []);
  const load = useCallback(() => api.get("/manager/overview").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const { rows: shops, Th } = useSort(d?.shops, SHOP_COLS, { key: "sale", dir: "desc", firstDir: FIRST_DESC });
  if (!d) return <p className="text-slate-500">Caricamento…</p>;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-[260px] flex-1">
        <div className="font-mono text-[11px] tracking-widest text-slate-500">PANNELLO MANAGER · {user.name}</div>
        <h1 className="text-4xl font-extrabold uppercase">Statistiche accorpate</h1>
        <p className="text-sm text-slate-500">{d.total.shops} negozi · margine = prezzo di vendita attuale del listino sito − prezzo d'acquisto del listino riservato del negozio. Se un prodotto non è nel listino riservato si usa il prezzo realmente pagato.</p>
      </div>
      <PriceListHeaderBtn lists={lists} />
      <GuideButton testid="manager-guide-btn" />
      </div>
      <ManagerPriceLists lists={lists} />
      <SyncBox onDone={load} />
      <Kpis s={d.total} prefix="overview" />
      <Charts s={d.total} />
      <div className="rounded-xl border bg-white overflow-x-auto">
        <h3 className="text-lg font-bold uppercase p-4 pb-0 flex items-center gap-2"><Store className="w-5 h-5" /> Negozi</h3>
        <table className="w-full text-sm mt-2" data-testid="manager-shops-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase font-mono text-slate-500">
            <tr><Th k="name" testid="shops-sort-name">Negozio</Th><Th k="orders" testid="shops-sort-orders">Ordini</Th><Th k="pieces" testid="shops-sort-pieces">Pezzi</Th><Th k="sale" testid="shops-sort-sale">Fatturato (listino)</Th><Th k="cost" testid="shops-sort-cost">Acquisto</Th><Th k="margin" testid="shops-sort-margin">Margine</Th><Th k="margin_pct" testid="shops-sort-margin-pct">%</Th><Th k="last_order" testid="shops-sort-last">Ultimo ordine</Th></tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <tr key={s.id} className="border-t hover:bg-slate-50">
                <td className="p-3"><Link to={`/manager/negozi/${s.id}`} data-testid={`manager-shop-link-${s.id}`} className="font-semibold text-brand-navy hover:underline">{s.name}</Link>{!s.has_price_list && <div className="text-[10px] text-slate-500">senza listino riservato</div>}</td>
                <td className="p-3 font-mono">{s.orders}</td><td className="p-3 font-mono">{s.pieces}</td>
                <td className="p-3">{eur(s.sale)}</td><td className="p-3">{eur(s.cost)}</td>
                <td className={`p-3 font-bold ${s.margin < 0 ? "text-rose-700" : "text-emerald-700"}`}>{eur(s.margin)}</td>
                <td className="p-3 font-mono">{s.margin_pct}%</td>
                <td className="p-3 text-xs whitespace-nowrap">{s.last_order ? fmtDate(s.last_order) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.shops.length && <p className="p-5 text-sm text-slate-500" data-testid="manager-no-shops">Nessun negozio assegnato. Chiedi all'amministratore di abilitarti.</p>}
      </div>
    </div>
  );
}

export function ManagerShop() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/manager/shops/${id}`).then((r) => alive && setD(r.data)).catch(() => alive && setD(false));
    return () => { alive = false; };
  }, [id]);
  const { rows: orders, Th } = useSort(d?.orders, ORDER_COLS, { key: "created_at", dir: "desc", firstDir: FIRST_DESC });
  if (d === false) return <p className="text-slate-500" data-testid="manager-shop-not-found">Negozio non trovato.</p>;
  if (!d) return <p className="text-slate-500">Caricamento…</p>;
  return (
    <div className="space-y-6" data-testid="manager-shop-detail">
      <Link to="/manager" className="text-sm text-slate-500 flex items-center gap-1 hover:text-brand-navy"><ArrowLeft className="w-4 h-4" /> Tutti i negozi</Link>
      <h1 className="text-4xl font-extrabold uppercase flex items-center gap-3"><TrendingUp className="w-8 h-8 text-brand-navy" /> {d.shop.name}</h1>
      <Kpis s={d.summary} prefix="shop" />
      <Charts s={d.summary} />
      <div className="rounded-xl border bg-white overflow-x-auto">
        <h3 className="text-lg font-bold uppercase p-4 pb-0">Storico ordini</h3>
        <table className="w-full text-sm mt-2" data-testid="manager-orders-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase font-mono text-slate-500">
            <tr><Th k="number" testid="orders-sort-number">Ordine</Th><Th k="created_at" testid="orders-sort-date">Data</Th><Th k="items" testid="orders-sort-items">Articoli</Th><Th k="sale" testid="orders-sort-sale">Venduto listino</Th><Th k="cost" testid="orders-sort-cost">Acquisto</Th><Th k="margin" testid="orders-sort-margin">Margine</Th><Th k="status" testid="orders-sort-status">Stato</Th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t align-top">
                <td className="p-3 font-mono font-semibold">{o.number}</td>
                <td className="p-3 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="p-3 text-xs">{o.lines.map((l, i) => <div key={i} data-testid={`manager-line-${o.number}-${i}`}>{l.sku && <span className="font-mono text-[10px] text-slate-500 mr-1">{l.sku}</span>}{l.name} × {l.quantity} <span className="text-slate-500">({eur(l.sale_unit)} → {eur(l.cost_unit)}{l.in_list ? " listino" : " pagato"})</span>{l.pads > 0 && <span className="text-slate-500"> + {l.pads} cuscinett{l.pads > 1 ? "i" : "o"}</span>}</div>)}</td>
                <td className="p-3">{eur(o.sale)}</td><td className="p-3">{eur(o.cost)}</td>
                <td className={`p-3 font-bold ${o.margin < 0 ? "text-rose-700" : "text-emerald-700"}`}>{eur(o.margin)}</td>
                <td className="p-3"><StatusBadge s={o.status} /></td>
                <td className="p-3"><button data-testid={`manager-order-doc-${o.number}`} onClick={() => downloadFile({ url: `/manager/orders/${o.id}/document` }, `ordine-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)))} className="press h-8 px-2 rounded-md border border-brand-navy text-xs font-semibold flex items-center gap-1 whitespace-nowrap"><FileDown className="w-3.5 h-3.5" /> PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.orders.length && <p className="p-5 text-sm text-slate-500">Nessun ordine per questo negozio.</p>}
      </div>
    </div>
  );
}
